/**
 * WpBridge
 *
 * Simple bridge for listening to WordPress → React custom DOM events.
 * Events are dispatched on `window` with name 'wp-event' and a detail object
 * that contains a `type` and optional payload.
 *
 * Example:
 *   window.dispatchEvent(new CustomEvent('wp-event', { detail: { type: 'add-to-cart', productId: 'SKU-1' } }))
 */

export const WP_EVENT_CHANNEL = 'wp-event' as const;

export type WpEventDetail = {
  type: string;
  [key: string]: unknown;
};

export type WpEventHandler = (payload: Record<string, unknown>) => void;

function isWindowAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.addEventListener === 'function';
}

function normalizePayload(detail: WpEventDetail): Record<string, unknown> {
  const { type: _ignored, ...payload } = detail;
  return payload;
}

export class WpBridge {
  private debug: boolean;

  constructor(options?: { debug?: boolean }) {
    this.debug = Boolean(options?.debug);
  }

  /**
   * Subscribe to a specific WP event type
   */
  public on(type: string, handler: WpEventHandler): () => void {
    if (!isWindowAvailable()) {
      return () => undefined;
    }

    const listener = (event: Event) => {
      const customEvent = event as CustomEvent<WpEventDetail>;
      const detail = customEvent.detail;

      if (!detail || detail.type !== type) {
        return;
      }

      const payload = normalizePayload(detail);
      if (this.debug) {
        console.info('[WpBridge] event', type, payload);
      }
      handler(payload);
    };

    window.addEventListener(WP_EVENT_CHANNEL, listener as EventListener);
    if (this.debug) {
      console.info('[WpBridge] on', type);
    }

    return () => {
      window.removeEventListener(WP_EVENT_CHANNEL, listener as EventListener);
      if (this.debug) {
        console.info('[WpBridge] off', type);
      }
    };
  }

  /**
   * Subscribe once only
   */
  public once(type: string, handler: WpEventHandler): () => void {
    const unsubscribe = this.on(type, (payload) => {
      try {
        handler(payload);
      } finally {
        unsubscribe();
      }
    });
    return unsubscribe;
  }

  /**
   * Dispatch helper for tests/manual triggers
   */
  public dispatch(type: string, payload?: Record<string, unknown>): void {
    if (!isWindowAvailable()) {
      return;
    }
    const detail: WpEventDetail = { type, ...payload };
    window.dispatchEvent(new CustomEvent(WP_EVENT_CHANNEL, { detail }));
  }
}

// Singleton for convenience
export const wpBridge = new WpBridge();
